import { resolveLocalStorage } from "@bindery/shared/web-storage";
import { Dexie } from "dexie";
import type { SyncLogger } from "@/sync/retry";
import { readMeta, writeMeta, type ReaderDb } from "./db";
import { nicknameKey } from "./nickname";

export const LEGACY_DATABASES = [
  "LearningRecordsDexie",
  "FluencyRecordsDexie",
  "CachedAssetsDatabase",
] as const;

// `__m` is Legend State's metadata sibling of a persisted key
// (MetadataSuffix in @legendapp/state's local-storage plugin).
const LEGACY_KEYS: ReadonlySet<string> = new Set([
  "session",
  "session__m",
  "library",
  "library__m",
  "pwa-install-dismissed-at",
  "pwa-install-card-shown-session",
]);
const LEGACY_KEY_PREFIXES = ["collapsible-card-", "resizable-layout-"];
const LEGACY_NICKNAME_PREFIX = "readerNickname:";

export const LEGACY_CLEANUP_META_KEY = "legacyCleanupAt";

type CleanupStorage = Pick<
  Storage,
  "getItem" | "setItem" | "removeItem" | "key" | "length"
>;

export interface LegacyCleanupDeps {
  db: ReaderDb;
  logger: SyncLogger;
  storage?: CleanupStorage | null;
  deleteDatabase?: (name: string) => Promise<void>;
  now?: () => number;
}

const isLegacyKey = (key: string): boolean =>
  LEGACY_KEYS.has(key) ||
  LEGACY_KEY_PREFIXES.some((prefix) => key.startsWith(prefix));

function storageKeys(storage: CleanupStorage): string[] {
  return Array.from({ length: storage.length }, (_, index) =>
    storage.key(index),
  ).filter((key): key is string => key !== null);
}

// Never touches a key under `rbe-reader.`: the logging session and visit
// ids live there and must survive the switch from ../app.
function cleanStorage(storage: CleanupStorage): {
  removed: number;
  nicknames: number;
} {
  let removed = 0;
  let nicknames = 0;
  for (const key of storageKeys(storage)) {
    if (key.startsWith(LEGACY_NICKNAME_PREFIX)) {
      const readerId = key.slice(LEGACY_NICKNAME_PREFIX.length);
      const nickname = storage.getItem(key);
      if (
        nickname !== null &&
        storage.getItem(nicknameKey(readerId)) === null
      ) {
        storage.setItem(nicknameKey(readerId), nickname);
        nicknames += 1;
      }
      storage.removeItem(key);
      removed += 1;
    } else if (isLegacyKey(key)) {
      storage.removeItem(key);
      removed += 1;
    }
  }
  return { removed, nicknames };
}

/** Removes ../app's databases and keys once per device; safe to re-run. */
export async function legacyCleanup({
  db,
  logger,
  storage = resolveLocalStorage(),
  deleteDatabase = (name) => Dexie.delete(name),
  now = Date.now,
}: LegacyCleanupDeps): Promise<void> {
  if ((await readMeta<number>(db, LEGACY_CLEANUP_META_KEY)) !== undefined) {
    return;
  }
  const deletions = await Promise.allSettled(
    LEGACY_DATABASES.map((name) => deleteDatabase(name)),
  );
  const failedDatabases = LEGACY_DATABASES.filter(
    (_, index) => deletions[index]?.status === "rejected",
  );
  let keys = { removed: 0, nicknames: 0 };
  let storageFailed = false;
  try {
    if (storage) keys = cleanStorage(storage);
  } catch {
    storageFailed = true;
  }
  const complete = failedDatabases.length === 0 && !storageFailed;
  if (complete) await writeMeta(db, LEGACY_CLEANUP_META_KEY, now());
  logger.event("sync.legacy.cleanup", {
    outcome: complete ? "done" : "retry_next_boot",
    failed_databases: failedDatabases,
    storage_failed: storageFailed,
    keys_removed: keys.removed,
    nicknames_copied: keys.nicknames,
  });
}
